import React, { useEffect, useRef, useState } from 'react';
import { Button, Form, Input, message, Modal, Pagination, Table } from 'antd';
import { errorHandle } from '../../services';
import { fetchScheduleSkuPage, fetchScheduleSpuPage } from './api';
import { ProductSelectItem } from './product-select-modal';
import { PAGE_SIZE_OPTIONS } from './constants';

type DimType = 'SPU' | 'SKU';

interface ScheduleProductSelectModalProps {
    visible: boolean;
    dim: DimType;
    defaultSelected?: ProductSelectItem[];
    onCancel: () => void;
    onOk: (selected: ProductSelectItem[]) => void;
}

const toItem = (row: any, dim: DimType): ProductSelectItem => {
    if (dim === 'SPU') {
        return {
            code: row.spu_code || row.spuCode || row.spu || '',
            name: row.product_name || row.productName || row.goodsName || row.name || '',
            skuCount: row.sku_count ?? row.skuCount,
        };
    }
    return {
        code: row.sku_code || row.skuCode || row.sku || '',
        name: row.product_name || row.productName || row.name || row.goodsName || '',
        sknCode: row.skn_code || row.sknCode || '',
        showName: row.show_name || row.showName || '',
    };
};

const pickList = (res: any): { list: any[]; total: number } => {
    const raw = res?.result;
    const list = Array.isArray(raw) ? raw : raw?.result || res?.resultList || [];
    const total = res?.totalNum || res?.total_num || raw?.totalNum || res?.total || 0;
    return { list, total };
};

export const ScheduleProductSelectModal = ({
    visible,
    dim,
    defaultSelected = [],
    onCancel,
    onOk,
}: ScheduleProductSelectModalProps) => {
    return (
        <ScheduleProductSelectModalInner
            key={dim}
            visible={visible}
            dim={dim}
            defaultSelected={defaultSelected}
            onCancel={onCancel}
            onOk={onOk}
        />
    );
};

const ScheduleProductSelectModalInner = ({
    visible,
    dim,
    defaultSelected = [],
    onCancel,
    onOk,
}: ScheduleProductSelectModalProps) => {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<ProductSelectItem[]>([]);
    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [total, setTotal] = useState(0);
    const [codeKeyword, setCodeKeyword] = useState('');
    const [nameKeyword, setNameKeyword] = useState('');
    const [searchCode, setSearchCode] = useState('');
    const [searchName, setSearchName] = useState('');
    const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
    const [allSelected, setAllSelected] = useState<ProductSelectItem[]>([]);
    const requestSeq = useRef(0);

    const resetTable = () => {
        setRows([]);
        setTotal(0);
        setPageNum(1);
        setCodeKeyword('');
        setNameKeyword('');
        setSearchCode('');
        setSearchName('');
        setSelectedRowKeys([]);
        setAllSelected([]);
    };

    const loadData = (
        page = pageNum,
        size = pageSize,
        code = searchCode,
        name = searchName,
        selected = allSelected
    ) => {
        const seq = ++requestSeq.current;
        setLoading(true);
        setRows([]);
        setTotal(0);
        const params = {
            page,
            per_page: size,
            ...(code.trim() || name.trim() ? { keyword: code.trim() || name.trim() } : {}),
        };
        const request = dim === 'SPU' ? fetchScheduleSpuPage(params) : fetchScheduleSkuPage(params);

        request
            .then((res: any) => {
                if (seq !== requestSeq.current) {
                    return;
                }
                const { list, total: nextTotal } = pickList(res);
                const mapped = list
                    .map((item: any) => toItem(item, dim))
                    .filter((item) => item.code);
                setRows(mapped);
                setTotal(nextTotal);
                setSelectedRowKeys(
                    mapped
                        .filter((item) => selected.some((sel) => sel.code === item.code))
                        .map((item) => item.code)
                );
            })
            .catch((err: any) => {
                if (seq !== requestSeq.current) {
                    return;
                }
                setRows([]);
                setTotal(0);
                errorHandle(err);
            })
            .finally(() => {
                if (seq === requestSeq.current) {
                    setLoading(false);
                }
            });
    };

    useEffect(() => {
        if (!visible) {
            requestSeq.current += 1;
            resetTable();
            setLoading(false);
            return;
        }
        const defaults = (defaultSelected || []).filter((item) => item.code);
        setAllSelected(defaults);
        setSelectedRowKeys(defaults.map((item) => item.code));
        setCodeKeyword('');
        setNameKeyword('');
        setSearchCode('');
        setSearchName('');
        setPageNum(1);
        loadData(1, pageSize, '', '', defaults);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, dim]);

    const handleSearch = () => {
        setSearchCode(codeKeyword);
        setSearchName(nameKeyword);
        setPageNum(1);
        loadData(1, pageSize, codeKeyword, nameKeyword);
    };

    const handleReset = () => {
        setCodeKeyword('');
        setNameKeyword('');
        setSearchCode('');
        setSearchName('');
        setPageNum(1);
        loadData(1, pageSize, '', '');
    };

    const handleRowSelectionChange = (
        keys: string[] | number[],
        selectedRows: ProductSelectItem[]
    ) => {
        const selectedKeys = (keys as Array<string | number>).map((key) => String(key));
        const currentPageUnselected = rows
            .filter((item) => !selectedKeys.includes(item.code))
            .map((item) => item.code);
        const kept = allSelected.filter((item) => !currentPageUnselected.includes(item.code));
        const newlyAdded = selectedRows.filter(
            (item) => !kept.some((sel) => sel.code === item.code)
        );
        setSelectedRowKeys(selectedKeys);
        setAllSelected([...kept, ...newlyAdded]);
    };

    const handleOk = () => {
        if (!allSelected.length) {
            message.warning(`请至少选择一个 ${dim}`);
            return;
        }
        onOk(allSelected);
    };

    const columns =
        dim === 'SPU'
            ? [
                  {
                      title: 'SPU',
                      dataIndex: 'code',
                      key: 'code',
                      width: 180,
                  },
                  {
                      title: '商品名称',
                      dataIndex: 'name',
                      key: 'name',
                  },
                  {
                      title: 'SKU 数',
                      dataIndex: 'skuCount',
                      key: 'skuCount',
                      width: 100,
                  },
              ]
            : [
                  {
                      title: 'SKU',
                      dataIndex: 'code',
                      key: 'code',
                      width: 160,
                  },
                  {
                      title: 'SKN',
                      dataIndex: 'sknCode',
                      key: 'sknCode',
                      width: 140,
                  },
                  {
                      title: '商品名称',
                      dataIndex: 'name',
                      key: 'name',
                  },
                  {
                      title: '展示名称',
                      dataIndex: 'showName',
                      key: 'showName',
                  },
              ];

    return (
        <Modal
            title={`选择 ${dim}`}
            visible={visible}
            onCancel={onCancel}
            onOk={handleOk}
            width={dim === 'SKU' ? 960 : 760}
            zIndex={1100}
            destroyOnClose
            okText={`确认选择（${allSelected.length}）`}
            cancelText="取消"
        >
            <Form layout="inline" style={{ marginBottom: 16 }}>
                <Form.Item label={`${dim}编码`}>
                    <Input
                        allowClear
                        placeholder={`${dim}编码`}
                        value={codeKeyword}
                        onChange={(e) => setCodeKeyword(e.target.value)}
                        onPressEnter={handleSearch}
                        style={{ width: 180 }}
                    />
                </Form.Item>
                <Form.Item label="名称">
                    <Input
                        allowClear
                        placeholder="商品名称"
                        value={nameKeyword}
                        onChange={(e) => setNameKeyword(e.target.value)}
                        onPressEnter={handleSearch}
                        style={{ width: 180 }}
                    />
                </Form.Item>
                <Form.Item>
                    <Button type="primary" onClick={handleSearch}>
                        查询
                    </Button>
                    <Button style={{ marginLeft: 8 }} onClick={handleReset}>
                        重置
                    </Button>
                </Form.Item>
            </Form>
            <Table
                rowKey="code"
                columns={columns}
                dataSource={rows}
                loading={loading}
                pagination={false}
                scroll={{ y: 360 }}
                rowSelection={{
                    type: 'checkbox',
                    selectedRowKeys,
                    onChange: handleRowSelectionChange,
                }}
            />
            {total > 0 && (
                <div style={{ marginTop: 16, textAlign: 'right' }}>
                    <Pagination
                        current={pageNum}
                        pageSize={pageSize}
                        total={total}
                        showSizeChanger
                        showQuickJumper
                        pageSizeOptions={PAGE_SIZE_OPTIONS}
                        onChange={(page, size) => {
                            setPageNum(page);
                            if (size !== pageSize) {
                                setPageSize(size);
                            }
                            loadData(page, size, searchCode, searchName);
                        }}
                        onShowSizeChange={(current, size) => {
                            setPageNum(1);
                            setPageSize(size);
                            loadData(1, size, searchCode, searchName);
                        }}
                    />
                </div>
            )}
        </Modal>
    );
};
